import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import { ChevronLeft, ChevronRight, Sunrise } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { MetricBig, MetricCard, MetricRow, MetricSmall } from '@/components/report/MetricCard';
import { ImageLightbox } from '@/components/ui/image-lightbox';
import { cn } from '@/lib/utils';
import { getStaffArrival, type StaffArrivalDayDto } from '@/api/analytics';
import { Tooltip } from '@/components/ui/tooltip';

/** Плашка «Приход к открытию» (Алексей 08.10, #1324; компактнее — 12.10, #1432): один день —
 *  время, чип и фото одной строкой; период — сводка чипами и короткие строки по дням.
 *  Рисуется только у филиалов с включённой функцией и данными (#1341). */

// Время точки — по Москве, как у кадров (hover-time.tsx): у зрителя в другом поясе
// плашка иначе расходилась бы с подписями фото и графиком.
function fmtTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' });
}
function fmtDay(ymd: string): string {
  const [, m, d] = ymd.split('-');
  return `${d}.${m}`;
}

/** До скольких минут опоздание — «чуть позже», жёлто-зелёным (Алексей 12.10, #1444). */
const SLIGHTLY_LATE_MIN = 5;

/** Чип состояния дня: вовремя / чуть позже / +N мин / никого / ждём. */
function StatusChip({ d }: { d: StaffArrivalDayDto }) {
  const base = 'inline-flex h-5 shrink-0 items-center rounded-md px-1.5 text-[11px] font-medium tabular-nums';
  if (d.status === 'pending') return <span className={cn(base, 'bg-muted text-muted-foreground')}>ждём к {fmtTime(d.expected_at)}</span>;
  if (d.status === 'nobody') return <span className={cn(base, 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300')}>никого</span>;
  if (d.already) return <span className={cn(base, 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300')}>уже были на месте</span>;
  // Зелёным, как «вовремя» (Алексей 26.10 #2605): люди уже за баром на первом кадре.
  if (isAlready(d)) return <span className={cn(base, 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300')}>к старту съёмки были на месте</span>;
  const delta = d.delta_min ?? 0;
  if (delta <= 0) return <span className={cn(base, 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300')}>{delta < 0 ? `−${Math.abs(delta)} мин` : 'вовремя'}</span>;
  if (delta <= SLIGHTLY_LATE_MIN) return <span className={cn(base, 'bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300')}>чуть позже · +{delta} мин</span>;
  return <span className={cn(base, 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300')}>+{delta} мин</span>;
}

/** Люди уже на первом кадре окна, а окно началось ПОСЛЕ назначенного времени — время прихода
 *  неизвестно (Астра 23.10, #2285/#2286). Если окно началось вовремя, BE отдаёт on_time с
 *  `already` — проверка пройдена, это зелёный (Алексей 25.10 #2324). */
const isAlready = (d: StaffArrivalDayDto) => d.status === 'no_frames' && !!d.first_seen_at;
/** Дни, которые показываем: «камеры не снимали» без людей — пропуск (Алексей 12.10, #1444);
 *  «уже были к началу съёмки» — показываем. */
const visibleDays = (days: StaffArrivalDayDto[]) => days.filter((d) => d.status !== 'no_frames' || isAlready(d));

/** Миниатюра дня. У опоздания — два кадра рядом: назначенное время (за баром пусто) и приход
 *  (Алексей 26.10 #2612/#2613 «везде, где опоздания»); клик открывает лайтбокс на нужном кадре. */
function Thumb({ d, onPhoto, size = 'sm' }: { d: StaffArrivalDayDto; onPhoto: (d: StaffArrivalDayDto, k: number) => void; size?: 'sm' | 'md' }) {
  if (!d.photo_url) return null;
  const srcs = d.photo_is_start && d.arrival_photo_url ? [d.photo_url, d.arrival_photo_url] : [d.photo_url];
  const times = srcs.length > 1 ? [d.photo_at, d.first_seen_at] : [];
  return (
    <span className="flex shrink-0 gap-0.5">
      {srcs.map((src, k) => (
        <button key={k} type="button" onClick={() => onPhoto(d, k)} className="relative shrink-0 overflow-hidden rounded-md ring-1 ring-border hover:ring-primary/50" aria-label="Открыть фото">
          <img src={src} alt="" className={cn('object-cover', size === 'md' ? 'h-14 w-24' : 'h-9 w-14')} loading="lazy" />
          {times[k] && <span className="absolute bottom-0 left-0 rounded-tr bg-background/85 px-0.5 text-[9px] leading-tight tabular-nums text-foreground">{fmtTime(times[k]!)}</span>}
        </button>
      ))}
    </span>
  );
}

function captionOf(d: StaffArrivalDayDto): string {
  if (d.photo_is_start && d.photo_at) {
    // Опоздание: фото на назначенное время — за баром ещё никого (Алексей 26.10 #2604).
    return `${fmtDay(d.date)} · ${fmtTime(d.photo_at)}, к назначенному времени за баром никого${d.text ? ` · ${d.text}` : ''}`;
  }
  return `${fmtDay(d.date)} · ${d.first_seen_at ? `первые за баром ${fmtTime(d.first_seen_at)}` : 'никого'}${d.text ? ` · ${d.text}` : ''}`;
}

/** Фото дня для просмотра: на назначенное время и, если опоздали, кадр прихода вторым. */
function shotsOf(d: StaffArrivalDayDto): { srcs: string[]; captions: string[] } {
  const srcs = [d.photo_url!];
  const captions = [captionOf(d)];
  if (d.photo_is_start && d.arrival_photo_url && d.first_seen_at) {
    srcs.push(d.arrival_photo_url);
    captions.push(`${fmtDay(d.date)} · первые за баром ${fmtTime(d.first_seen_at)}`);
  }
  return { srcs, captions };
}

export function StaffArrivalCard({ zoneId, from, to, compact }: { zoneId: string; from: string; to: string; compact?: boolean }) {
  const { data } = useSWR(zoneId ? ['staff-arrival', zoneId, from, to] : null, () => getStaffArrival(zoneId, from, to).then((r) => r.data), { revalidateOnFocus: false });
  const [photo, setPhoto] = useState<StaffArrivalDayDto | null>(null);
  const [shot, setShot] = useState(0);
  // Нет ни одной записи за день/период — плашки нет вовсе (Алексей #1341).
  if (!data || !data.enabled || !data.days.length) return null;
  const single = from === to;
  const days = visibleDays(data.days);
  if (!days.length) return null;
  const s = data.summary;
  const d0 = days[0];
  const open = (d: StaffArrivalDayDto, k = 0) => { setShot(k); setPhoto(d); };
  return (
    <Card>
      <CardContent className={cn(compact ? 'p-3' : 'px-4 py-3')}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Sunrise className="size-4 text-emerald-600" />
            <span>Приход к открытию</span>
          </div>
          {single ? (
            <>
              <span className="text-xs text-muted-foreground">сотрудники к {fmtTime(d0.expected_at)}</span>
              <div className="ml-auto flex items-center gap-3">
                {d0.first_seen_at ? (
                  <span className="text-sm tabular-nums"><span className="font-semibold">{fmtTime(d0.first_seen_at)}</span><span className="text-muted-foreground"> · {d0.first_seen_people} чел.</span></span>
                ) : (
                  <span className="text-sm text-muted-foreground">{d0.status === 'pending' ? 'ещё никого' : d0.status === 'no_frames' ? 'камер не было' : 'никого за баром'}</span>
                )}
                <StatusChip d={d0} />
                <Thumb d={d0} onPhoto={open} size="md" />
              </div>
            </>
          ) : (
            <div className="ml-auto flex flex-wrap items-center gap-1.5 text-[11px]">
              <span className="rounded-md bg-muted px-1.5 py-0.5 text-muted-foreground">{s.days} дн.</span>
              <span className="rounded-md bg-emerald-100 px-1.5 py-0.5 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">вовремя {s.early_or_on_time}</span>
              <span className="rounded-md bg-amber-100 px-1.5 py-0.5 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">позже {s.late}</span>
              {s.nobody > 0 && <span className="rounded-md bg-red-100 px-1.5 py-0.5 text-red-800 dark:bg-red-900/30 dark:text-red-300">никого {s.nobody}</span>}
              {s.avg_delta_min != null && <span className="text-muted-foreground">в среднем {s.avg_delta_min > 0 ? `+${s.avg_delta_min}` : s.avg_delta_min} мин</span>}
            </div>
          )}
        </div>
        {!single && (
          <div className="mt-2 grid grid-cols-[auto_auto_1fr_auto] items-center gap-x-3 gap-y-1 text-sm">
            {days.slice(-14).reverse().map((d) => (
              <React.Fragment key={d.id}>
                <span className="tabular-nums text-muted-foreground">{fmtDay(d.date)}</span>
                <span className="tabular-nums">{d.first_seen_at ? fmtTime(d.first_seen_at) : '—'}</span>
                <StatusChip d={d} />
                <Thumb d={d} onPhoto={open} />
              </React.Fragment>
            ))}
          </div>
        )}
      </CardContent>
      {photo && photo.photo_url && (
        <ImageLightbox {...shotsOf(photo)} index={shot} onClose={() => setPhoto(null)} onNavigate={(i) => { if (i >= 0 && i < shotsOf(photo).srcs.length) setShot(i); }} />
      )}
    </Card>
  );
}

/** Показатель «Приход к открытию» для сводных показателей вкладки «Очередь» (Алексей 12.10, #1444):
 *  та же карточка-метрика, что «Расчётное время в очереди», — крупное время, чип, фото.
 *
 *  Справа — фото ВЫБРАННОГО дня (Алексей 20.10, #1593): пилюля дня выбирает день, фото
 *  переключается, клик по фото — полноэкранный лайтбокс с листанием по дням. Длинный период
 *  (больше {@link HEATMAP_FROM_DAYS} дней) пилюлями не влезает — вместо них и фото справа
 *  календарная тепловая карта по дням (недели × дни недели), клик по клетке открывает фото. */
const HEATMAP_FROM_DAYS = 14;

type Tone = 'nobody' | 'late' | 'slight' | 'ok' | 'pending' | 'already';
function toneOf(d: StaffArrivalDayDto): Tone {
  if (d.status === 'pending') return 'pending';
  if (isAlready(d)) return 'already';
  if (d.status === 'nobody') return 'nobody';
  const delta = d.delta_min ?? 0;
  return delta > SLIGHTLY_LATE_MIN ? 'late' : delta > 0 ? 'slight' : 'ok';
}
const TONE_CLS: Record<Tone, string> = {
  nobody: 'border-red-200 bg-red-50 text-red-800 dark:border-red-900/50 dark:bg-red-900/30 dark:text-red-300',
  late: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/30 dark:text-amber-300',
  slight: 'border-lime-200 bg-lime-50 text-lime-800 dark:border-lime-900/50 dark:bg-lime-900/30 dark:text-lime-300',
  ok: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-900/30 dark:text-emerald-300',
  pending: 'border-border bg-muted text-muted-foreground',
  already: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-900/30 dark:text-emerald-300',
};
const CELL_CLS: Record<Tone, string> = {
  nobody: 'bg-red-400 dark:bg-red-500',
  late: 'bg-amber-400 dark:bg-amber-500',
  slight: 'bg-lime-400 dark:bg-lime-500',
  ok: 'bg-emerald-500 dark:bg-emerald-500',
  pending: 'bg-muted-foreground/30',
  already: 'bg-emerald-500 dark:bg-emerald-500',
};
const WEEKDAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];

/** Понедельник недели даты (UTC-арифметика по 'YYYY-MM-DD', без сдвигов пояса). */
function mondayOf(ymd: string): number {
  const t = Date.UTC(+ymd.slice(0, 4), +ymd.slice(5, 7) - 1, +ymd.slice(8, 10));
  const wd = (new Date(t).getUTCDay() + 6) % 7;
  return t - wd * 86400000;
}
function weekdayOf(ymd: string): number {
  return (new Date(Date.UTC(+ymd.slice(0, 4), +ymd.slice(5, 7) - 1, +ymd.slice(8, 10))).getUTCDay() + 6) % 7;
}

/** Календарная тепловая карта прихода: колонка — неделя, строка — день недели. */
function ArrivalHeatmap({ days, sel, onPick }: { days: StaffArrivalDayDto[]; sel: number; onPick: (i: number) => void }) {
  const weeks = useMemo(() => {
    const byWeek = new Map<number, (number | null)[]>();
    days.forEach((d, i) => {
      const w = mondayOf(d.date);
      if (!byWeek.has(w)) byWeek.set(w, Array<number | null>(7).fill(null));
      byWeek.get(w)![weekdayOf(d.date)] = i;
    });
    return [...byWeek.entries()].sort((a, b) => a[0] - b[0]);
  }, [days]);
  const label = (w: number) => { const d = new Date(w); return `${String(d.getUTCDate()).padStart(2, '0')}.${String(d.getUTCMonth() + 1).padStart(2, '0')}`; };
  return (
    <div className="flex gap-1.5 text-[10px] text-muted-foreground">
      <div className="grid grid-rows-7 gap-0.5 pt-4">
        {WEEKDAYS.map((w) => <span key={w} className="flex h-3.5 items-center leading-none">{w}</span>)}
      </div>
      <div className="flex gap-0.5 overflow-x-auto">
        {weeks.map(([w, cells]) => (
          <div key={w} className="flex flex-col gap-0.5">
            <span className="h-3.5 whitespace-nowrap leading-none tabular-nums">{label(w)}</span>
            {cells.map((i, wd) => {
              if (i == null) return <span key={wd} className="size-3.5 rounded-sm bg-muted/40" />;
              const d = days[i];
              return (
                <Tooltip key={wd} content={`${fmtDay(d.date)} · ${d.first_seen_at ? `первые за баром ${fmtTime(d.first_seen_at)}` : 'никого'}${d.text ? ` · ${d.text}` : ''}`}><button type="button" onClick={() => onPick(i)}
                  aria-label={`${fmtDay(d.date)} · ${d.first_seen_at ? `первые за баром ${fmtTime(d.first_seen_at)}` : 'никого'}${d.text ? ` · ${d.text}` : ''}`}
                  className={cn('size-3.5 rounded-sm transition hover:scale-110', CELL_CLS[toneOf(d)], i === sel && 'ring-2 ring-foreground ring-offset-1 ring-offset-background')} /></Tooltip>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/** `title`/`className` — для вставки в панель Статуса (#1862): там своя шапка «Приход к открытию». */
export function StaffArrivalSummaryCard({ zoneId, from, to, title = 'Приход к открытию', className }: { zoneId: string; from: string; to: string; title?: string; className?: string }) {
  const { data } = useSWR(zoneId ? ['staff-arrival', zoneId, from, to] : null, () => getStaffArrival(zoneId, from, to).then((r) => r.data), { revalidateOnFocus: false });
  // Выбранный день (индекс в days) и открытый лайтбокс. -1 = последний день: при смене периода
  // выбор не должен указывать на день, которого больше нет.
  const [selRaw, setSel] = useState(-1);
  const [lightbox, setLightbox] = useState<number | null>(null);
  if (!data || !data.enabled) return null;
  const days = visibleDays(data.days);
  if (!days.length) return null;
  const single = from === to;
  const s = data.summary;
  const d0 = days[0];
  const sel = selRaw >= 0 && selRaw < days.length ? selRaw : days.length - 1;
  const cur = days[sel];
  const curTwo = !!(cur?.photo_is_start && cur.photo_url && cur.arrival_photo_url);
  const lateDays = days.filter((d) => (d.delta_min ?? 0) > SLIGHTLY_LATE_MIN && d.status !== 'nobody' && d.status !== 'pending');
  const slightDays = days.filter((d) => (d.delta_min ?? 0) > 0 && (d.delta_min ?? 0) <= SLIGHTLY_LATE_MIN && d.status !== 'nobody' && d.status !== 'pending');
  const alreadyDays = days.filter(isAlready);
  const judged = days.length - alreadyDays.length;
  const heat = days.length > HEATMAP_FROM_DAYS;
  const pick = (i: number) => setSel(i);
  // Лайтбокс листает только дни с фото (на индексе без картинки он не рисуется вовсе), поэтому
  // его индекс — позиция в photoIdx, а выбор на пилюлях/карте пересчитывается через неё.
  const photoIdx = days.map((d, i) => (d.photo_url ? i : -1)).filter((i) => i >= 0);
  // Лайтбокс листает кадры, а не дни: у опоздания их два — назначенное время и приход (Алексей 26.10 #2612).
  const shots = photoIdx.flatMap((i) => { const s = shotsOf(days[i]); return s.srcs.map((src, k) => ({ day: i, k, src, caption: s.captions[k] })); });
  const openAt = (i: number, k = 0) => { setSel(i); const pos = shots.findIndex((x) => x.day === i && x.k === k); if (pos >= 0) setLightbox(pos); };
  return (
    <MetricCard icon={Sunrise} title={title} className={className}
      hint={`Когда первые сотрудники появились за баром относительно назначенного времени (открытие минус ${single ? '' : `${data.minutes_default} мин, `}«быть за N минут» из настроек филиала). Вовремя — не позже ${SLIGHTLY_LATE_MIN} мин. Справа фото выбранного дня: клик — на весь экран, стрелки листают дни.`}
      right={single ? <span className="text-xs text-muted-foreground">к {fmtTime(d0.expected_at)}</span> : undefined}>
      {single ? (
        <div className="flex items-end gap-4">
          <div className="min-w-0 flex-1">
            <MetricBig value={d0.first_seen_at ? fmtTime(d0.first_seen_at) : '—'} label={d0.already ? 'уже были на месте к назначенному' : isAlready(d0) ? 'к старту съёмки были на месте' : d0.first_seen_at ? `первые за баром · ${d0.first_seen_people} чел.` : d0.status === 'pending' ? 'ещё никого' : 'никого за баром'} className={d0.status === 'nobody' ? 'text-red-700 dark:text-red-300' : (d0.delta_min ?? 0) > SLIGHTLY_LATE_MIN ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300'} />
            <div className="mt-2"><StatusChip d={d0} /></div>
          </div>
          <Thumb d={d0} onPhoto={(_, k) => openAt(0, k)} size="md" />
        </div>
      ) : (
        <div className="flex items-start gap-4">
          <div className="min-w-0 flex-1">
            <MetricRow>
              <MetricBig value={s.early_or_on_time + slightDays.length} unit={`из ${judged} дн.`} label="вовремя" className="text-emerald-700 dark:text-emerald-300" />
              <MetricSmall value={lateDays.length} label={`позже ${SLIGHTLY_LATE_MIN} мин`} className={lateDays.length ? 'text-amber-700 dark:text-amber-300' : undefined} />
              {s.nobody > 0 && <MetricSmall value={s.nobody} label="никого" className="text-red-700 dark:text-red-300" />}
              {alreadyDays.length > 0 && <MetricSmall value={alreadyDays.length} label="к старту съёмки были на месте" className="text-emerald-700 dark:text-emerald-300" />}
              {s.avg_delta_min != null && <MetricSmall value={s.avg_delta_min > 0 ? `+${s.avg_delta_min}` : s.avg_delta_min} unit="мин" label="к открытию в среднем" />}
            </MetricRow>
            {heat ? (
              <div className="mt-3"><ArrivalHeatmap days={days} sel={sel} onPick={openAt} /></div>
            ) : (
              <div className="mt-3 flex flex-wrap gap-1">
                {days.map((d, i) => (
                  // Пилюля дня выбирает день (фото справа переключается); выбранная — с кольцом.
                  <Tooltip key={d.id} content={`${fmtDay(d.date)} · ${d.already || isAlready(d) ? (d.text ?? 'к старту съёмки были на месте') : d.first_seen_at ? `первые за баром ${fmtTime(d.first_seen_at)}` : 'никого'}`}><button type="button" onClick={() => pick(i)}
                    className={cn('inline-flex h-6 min-w-10 cursor-pointer items-center justify-center rounded-md border px-1.5 text-[11px] font-medium tabular-nums shadow-sm transition hover:-translate-y-px hover:shadow',
                      TONE_CLS[toneOf(d)], i === sel && 'ring-2 ring-foreground/70 ring-offset-1 ring-offset-background')}>
                    {fmtDay(d.date)}
                  </button></Tooltip>
                ))}
              </div>
            )}
          </div>
          {/* Фото выбранного дня. В режиме тепловой карты фото тоже остаётся: клетка выбирает день.
              Стрелки на самом фото листают дни на месте, а выбор на пилюлях/карте едет синхронно
              (Алексей 20.10, #1594). */}
          <div className={cn('flex shrink-0 flex-col items-stretch gap-1', curTwo ? 'w-60 sm:w-72' : 'w-36 sm:w-44')}>
            <div className="group relative overflow-hidden rounded-md ring-1 ring-border">
              {curTwo ? (
                // Опоздание — два кадра рядом: назначенное время (за баром пусто) и приход (#2611/#2612).
                <div className="grid grid-cols-2 gap-px bg-border">
                  {[{ src: cur.photo_url!, at: cur.photo_at, label: 'никого' }, { src: cur.arrival_photo_url!, at: cur.first_seen_at, label: 'пришли' }].map((x, k) => (
                    <button key={k} type="button" onClick={() => openAt(sel, k)} aria-label="Открыть фото на весь экран" className="relative block w-full">
                      <img src={x.src} alt="" className="aspect-[16/10] w-full object-cover transition hover:brightness-95" loading="lazy" />
                      <span className="absolute bottom-0.5 left-0.5 rounded bg-background/85 px-1 text-[10px] tabular-nums text-foreground">{fmtTime(x.at)} · {x.label}</span>
                    </button>
                  ))}
                </div>
              ) : cur.photo_url ? (
                <button type="button" onClick={() => openAt(sel)} aria-label="Открыть фото на весь экран" className="block w-full">
                  <img src={cur.photo_url} alt="" className="aspect-[16/10] w-full object-cover transition group-hover:brightness-95" loading="lazy" />
                </button>
              ) : (
                <div className="flex aspect-[16/10] w-full items-center justify-center bg-muted text-[11px] text-muted-foreground">нет фото</div>
              )}
              {sel > 0 && (
                <button type="button" aria-label="Предыдущий день" onClick={() => pick(sel - 1)}
                  className="absolute left-1 top-1/2 -translate-y-1/2 rounded-full bg-background/80 p-0.5 text-foreground shadow opacity-70 transition hover:opacity-100 hover:bg-background">
                  <ChevronLeft className="size-4" />
                </button>
              )}
              {sel < days.length - 1 && (
                <button type="button" aria-label="Следующий день" onClick={() => pick(sel + 1)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-background/80 p-0.5 text-foreground shadow opacity-70 transition hover:opacity-100 hover:bg-background">
                  <ChevronRight className="size-4" />
                </button>
              )}
            </div>
            <div className="flex items-center justify-between gap-1 text-[11px] tabular-nums text-muted-foreground">
              <span>{fmtDay(cur.date)}{!curTwo && cur.first_seen_at ? ` · ${fmtTime(cur.first_seen_at)}` : ''}</span>
              <StatusChip d={cur} />
            </div>
          </div>
        </div>
      )}
      {lightbox != null && shots.length > 0 && (
        <ImageLightbox srcs={shots.map((x) => x.src)} captions={shots.map((x) => x.caption)} index={Math.min(lightbox, shots.length - 1)}
          onClose={() => setLightbox(null)} onNavigate={(pos) => { if (pos >= 0 && pos < shots.length) { setLightbox(pos); setSel(shots[pos].day); } }} />
      )}
    </MetricCard>
  );
}

export default StaffArrivalCard;
